import { React, useState } from 'react';
import { Box, Typography, Card, Collapse, IconButton, Divider, CardContent, Alert, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, CheckCircle as CheckCircleIcon, ContentCopy as ContentCopyIcon, CheckOutlined as CheckOutlinedIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const StepConfirmation = ({
  partySize,
  arrivalDate,
  departureDate,
  totalPrice,
  customerForm,
  expandedConfirmation,
  setExpandedConfirmation,
  submissionResult,
  productName,
}) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopyKoriId = async () => {
    if (!submissionResult?.koriId) return;
    try {
      await navigator.clipboard.writeText(submissionResult.koriId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    catch (err) {
      console.error(t('varausModal.stepConfirmation.copyError'), err);
    }
  };

  if (submissionResult) {
    const { koriId, reservationData } = submissionResult;
    const product = reservationData.products[0];

    const formatDate = (dateStr) => {
      return dayjs(dateStr).format('DD.MM.YYYY');
    };

    const calculateNights = () => {
      const arrival = dayjs(product.arrivalDate);
      const departure = dayjs(product.departureDate);
      return departure.diff(arrival, 'days');
    };

    return (
      <Box sx={{ py: 3 }}>
        <Box
          sx={{
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'mb': 3,
            'animation': 'fadeIn 0.5s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'scale(0.95)' },
              to: { opacity: 1, transform: 'scale(1)' },
            },
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 48,
              color: 'success.main',
              mr: 1,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('varausModal.stepConfirmation.created')}
          </Typography>
        </Box>

        <Card sx={{ mb: 3, backgroundColor: 'success.lighter' }}>
          <CardContent>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {t('varausModal.stepConfirmation.basketNo')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace', flex: 1 }}>
                {koriId}
              </Typography>
              <Tooltip title={copied ? 'Kopioitu!' : 'Kopioi'} placement="top">
                <IconButton
                  onClick={handleCopyKoriId}
                  size="small"
                  sx={{
                    color: copied ? 'success.main' : 'primary.main',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {copied ? <CheckOutlinedIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('varausModal.stepConfirmation.resInfo')}
        </Typography>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('varausModal.stepConfirmation.prod')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {productName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('varausModal.stepConfirmation.arrival')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(product.arrivalDate)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('varausModal.stepConfirmation.departure')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDate(product.departureDate)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('varausModal.stepConfirmation.length')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {calculateNights()}
                  {' '}
                  {t('varausModal.stepConfirmation.nights')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('varausModal.components.personAmount')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {partySize}
                  {' '}
                  {t('varausModal.components.person')}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('varausModal.stepConfirmation.totalPrice')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {parseFloat(product.totalPrice).toFixed(2)}
                  {' '}
                  €
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('varausModal.stepConfirmation.customerInfo')}
        </Typography>

        <Card>
          <Box
            onClick={() => setExpandedConfirmation(!expandedConfirmation)}
            sx={{
              'display': 'flex',
              'justifyContent': 'space-between',
              'alignItems': 'center',
              'p': 2,
              'cursor': 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {expandedConfirmation ? t('varausModal.stepConfirmation.hideInfo') : t('varausModal.stepConfirmation.showInfo')}
            </Typography>
            <IconButton size="small">
              {expandedConfirmation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedConfirmation}>
            <Divider />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('varausModal.components.name')}
                  </Typography>
                  <Typography variant="body2">
                    {customerForm.firstName}
                    {' '}
                    {customerForm.lastName}
                  </Typography>
                </Box>
                {!!customerForm.company && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('varausModal.components.company')}
                    </Typography>
                    <Typography variant="body2">
                      {customerForm.company}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('varausModal.components.email')}
                  </Typography>
                  <Typography variant="body2">
                    {customerForm.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('varausModal.components.phone')}
                  </Typography>
                  <Typography variant="body2">
                    {customerForm.phoneNumber}
                  </Typography>
                </Box>
                {!!customerForm.address && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('varausModal.components.address')}
                    </Typography>
                    <Typography variant="body2">
                      {customerForm.address}
                      {' '}
                      {customerForm.postalCode}
                      {' '}
                      {customerForm.city}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('varausModal.stepConfirmation.resInfo')}
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('varausModal.stepConfirmation.prod')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {productName}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('varausModal.stepConfirmation.arrival')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dayjs(arrivalDate).format('DD.MM.YYYY')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('varausModal.stepConfirmation.departure')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dayjs(departureDate).format('DD.MM.YYYY')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('varausModal.stepConfirmation.length')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {dayjs(departureDate).diff(dayjs(arrivalDate), 'days')}
                {' '}
                {t('varausModal.stepConfirmation.nights')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('varausModal.components.personAmount')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {partySize}
                {' '}
                {t('varausModal.components.person')}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('varausModal.stepConfirmation.totalPrice')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {parseFloat(totalPrice).toFixed(2)}
                {' '}
                €
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('varausModal.stepConfirmation.customerInfo')}
      </Typography>

      <Card>
        <Box
          onClick={() => setExpandedConfirmation(!expandedConfirmation)}
          sx={{
            'display': 'flex',
            'justifyContent': 'space-between',
            'alignItems': 'center',
            'p': 2,
            'cursor': 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {expandedConfirmation ? t('varausModal.stepConfirmation.hideInfo') : t('varausModal.stepConfirmation.showInfo')}
          </Typography>
          <IconButton size="small">
            {expandedConfirmation ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expandedConfirmation}>
          <Divider />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('varausModal.components.name')}
                </Typography>
                <Typography variant="body2">
                  {customerForm.firstName}
                  {' '}
                  {customerForm.lastName}
                </Typography>
              </Box>
              {!!customerForm.company && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('varausModal.components.company')}
                  </Typography>
                  <Typography variant="body2">
                    {customerForm.company}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('varausModal.components.email')}
                </Typography>
                <Typography variant="body2">
                  {customerForm.email}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('varausModal.components.phone')}
                </Typography>
                <Typography variant="body2">
                  {customerForm.phoneNumber}
                </Typography>
              </Box>
              {!!customerForm.address && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('varausModal.components.address')}
                  </Typography>
                  <Typography variant="body2">
                    {customerForm.address}
                    {' '}
                    {customerForm.postalCode}
                    {' '}
                    {customerForm.city}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Collapse>
      </Card>

      <Alert severity="info" sx={{ mt: 2 }}>
        {t('varausModal.stepConfirmation.confirmCheck')}
      </Alert>
    </Box>
  );
};

export default StepConfirmation;
